import type {
  ImageType,
  ShelfType,
  StorePLP,
} from 'components/plpContext/types/storePLP';
import type { CollectionQuery } from 'lib/contentful/__generated__/Collection';
import type { CollectionInGridContentQuery } from 'lib/contentful/__generated__/CollectionInGridContent';
import type { CollectionSectionsQuery } from 'lib/contentful/__generated__/CollectionSections';
import type { CollectionShelvesQuery } from 'lib/contentful/__generated__/CollectionShelves';
import type {
  Collection,
  InGridContentItem,
} from 'lib/contentful/__generated__/types';
import type { Maybe } from 'lib/shopify-storefront/__generated__/types';
import type {
  InputContentfulSection,
  InputContentfulSections,
} from 'utils/types/contentfulSections';
import { formatCollectionIntroBlock } from './contentful/formatCollectionIntroBlock';
import { formatContentfulSections } from './contentful/formatContentfulSections';
import { formatShelf } from './contentful/formatShelf';
import { formatInGridContent } from './inGridContent';

type FormatContentfulCollectionQueriesArgs = {
  originalData: CollectionQuery | undefined;
  originalShelvesData?: CollectionShelvesQuery;
  originalSectionsData?: CollectionSectionsQuery;
  originalInGridData?: CollectionInGridContentQuery;
  proContent?: boolean;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const formatContentfulCollectionQueries = ({
  originalData,
  originalShelvesData,
  originalSectionsData,
  originalInGridData,
  proContent,
}: FormatContentfulCollectionQueriesArgs) => {
  if (!originalData?.collection?.items) return null;

  /**
   * Get main collection data.
   */
  const [collectionData] = originalData.collection.items;
  if (!collectionData) return null;

  const collection: StorePLP = {
    title: collectionData.title ?? '',
    merchandisedTitle:
      collectionData.merchandisedTitle ?? collectionData.title ?? '',
    description: collectionData.description ?? '',
    subtitle: collectionData.subtitle ?? '',
    backgroundColorOnMobile: collectionData?.backgroundColorOnMobile ?? true,
    backgroundColor: collectionData.backgroundColor ?? '',
    seo: {
      title: '',
    },
  };

  collection.mobileImage = collectionData.image as ImageType;
  collection.desktopImage = collectionData.desktopImage as ImageType;

  let textTheme: StorePLP['textTheme'] = 'dark';
  if (collectionData?.textTheme === 'light') {
    textTheme = 'light';
  }
  collection.textTheme = textTheme;

  /* Get Intro Block */
  const franchiseBlock = formatCollectionIntroBlock(
    collectionData as Collection
  );
  collection.franchiseBlock = franchiseBlock;

  /* Format Shelves and Shelf Sections */
  if (originalShelvesData?.collection?.items[0]?.shelfSections?.items) {
    const shelfSections =
      originalShelvesData.collection?.items[0]?.shelfSections.items;

    if (shelfSections) {
      /* Get Shelves Data */
      const shelves = formatShelves(shelfSections);
      collection.shelves = shelves;

      /* Get Sections & Shelves Data */
      const formattedShelfSections = formatContentfulSections(
        shelfSections as InputContentfulSections,
        proContent
      );
      collection.shelfSections = formattedShelfSections;
    }
  }

  /* Format Base Sections */
  if (originalSectionsData?.collection?.items[0]?.sections?.items) {
    const sections = originalSectionsData.collection.items[0].sections.items;
    collection.sections = formatContentfulSections(
      sections as InputContentfulSections,
      proContent
    );
  }

  /* Format In Grid Content */
  if (originalInGridData?.collection?.items[0]?.inGridContent?.items) {
    const inGridContent = formatInGridContent(
      originalInGridData?.collection?.items[0]?.inGridContent.items as Array<
        Maybe<InGridContentItem>
      >
    );

    collection.inGridContent = inGridContent;
  }

  return collection;
};

const formatShelves = (sections: Array<InputContentfulSection | null>) => {
  const shelves: ShelfType[] = [];

  sections.forEach((section) => {
    if (section?.__typename !== 'Shelf') return;

    const formattedShelf = formatShelf(section);

    shelves.push(formattedShelf);
  });

  return shelves;
};
